// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '../../../DataCache';
import { Account } from '../../../cache/Account';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { MarginGroupEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { MathUtils } from '@shared/utils/MathUtils';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { BaseAccountGroupController } from '../BaseControllers/BaseAccountGroupController';
import { RowItemDataNumberArray, RowItemDataStringArray } from '../../../cache/VerticalPanel/AccountDetails/RowItemData';
import { VerticalPanelColoringTypes } from '../../VerticalPanelColoringTypes';
import { StringUtils } from '@shared/utils/StringUtils';
import { GROUP } from '@shared/utils/Rules/AccountRules';
export class MarginGroupController extends BaseAccountGroupController {
    getGroupItemsTypes() {
        return [
            // MarginGroupEnum.FundsUsed,
            // MarginGroupEnum.FundsUsedPercent,
            MarginGroupEnum.MarginUsedNew,
            MarginGroupEnum.CurMargin,
            MarginGroupEnum.MaintMargin,
            // MarginGroupEnum.Leverage,
            MarginGroupEnum.MarginAvailableReal,
            MarginGroupEnum.MarginWarnPercent,
            MarginGroupEnum.MarginWarning,
            // MarginGroupEnum.MarginRequirement,
            // MarginGroupEnum.MinMargin,
            // MarginGroupEnum.BlockedByOrders,
            MarginGroupEnum.MarginStopOut,
            MarginGroupEnum.BlockedForStocks,
            MarginGroupEnum.StocksOrdersReq,
            MarginGroupEnum.StopOutPercent,
            // MarginGroupEnum.MarginDeficiency,
            // MarginGroupEnum.MarginSurplus,
            MarginGroupEnum.MarginForOthers,
            MarginGroupEnum.BlockedForStocksNew,
            MarginGroupEnum.OptionPremiumReq,
            MarginGroupEnum.WaivedMargin,
            MarginGroupEnum.WarningMarginReq,
            MarginGroupEnum.WarningMarginReqPercent,
            MarginGroupEnum.MarginBeforeWarning,
            MarginGroupEnum.InitialMarginWithoutWaived,
            MarginGroupEnum.StopOut,
            MarginGroupEnum.LiveLeverage,
            MarginGroupEnum.BlockedForFixedIncome,
            MarginGroupEnum.FixedIncomeOrdersReq
        ];
    }
    getDynamicItems() {
        return [];
    }
    getItemValue(type) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        switch (type) {
            case MarginGroupEnum.FundsUsed:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.FundsUsedPercent:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.MarginUsedNew:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.MarginUsed, curAccount, assetBalance));
            case MarginGroupEnum.StopOutPercent:
                return new RowItemDataNumberArray(curAccount.MarginLevel);
            case MarginGroupEnum.CurMargin:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.MarginUsedPercent, curAccount, assetBalance));
            case MarginGroupEnum.MaintMargin:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.RiskLevelPercent, curAccount, assetBalance));
            case MarginGroupEnum.Leverage:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.MarginAvailableReal:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.MarginAvailable, curAccount, assetBalance));
            case MarginGroupEnum.MarginWarnPercent:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.MarginWarningLevel, curAccount, assetBalance));
            case MarginGroupEnum.MarginWarning:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.WarningMargin, curAccount, assetBalance));
            case MarginGroupEnum.MarginRequirement:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.MinMargin:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.BlockedByOrders:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.MarginStopOut:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.TotalMaintReq, curAccount, assetBalance));
            case MarginGroupEnum.StocksOrdersReq:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.StocksOrdersReq, curAccount, assetBalance));
            case MarginGroupEnum.MarginDeficiency:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.MarginSurplus:
                return new RowItemDataNumberArray(NaN);
            case MarginGroupEnum.BlockedForStocks:
                return new RowItemDataNumberArray(curAccount.BlockedForStocksAccountCurrency / curAccount.getCrossPrice());
            case MarginGroupEnum.MarginForOthers: {
                const marginUsed = Account.GetAccountFeature(AccountFeature.MarginUsed, curAccount, assetBalance);
                return new RowItemDataNumberArray((marginUsed - curAccount.BlockedForStocksAccountCurrency) / curAccount.getCrossPrice());
            }
            case MarginGroupEnum.BlockedForStocksNew:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.BlockedForStocks, curAccount, assetBalance));
            case MarginGroupEnum.OptionPremiumReq:
                return new RowItemDataNumberArray(assetBalance.OptionPremiumReq);
            case MarginGroupEnum.WaivedMargin:
                return new RowItemDataNumberArray(curAccount.WaivedMargin);
            case MarginGroupEnum.WarningMarginReq:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.WarnMarginReq, curAccount, assetBalance));
            case MarginGroupEnum.WarningMarginReqPercent:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.WarnMarginReqPercent, curAccount, assetBalance));
            case MarginGroupEnum.MarginBeforeWarning:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.MarginBeforeWarning, curAccount, assetBalance));
            case MarginGroupEnum.InitialMarginWithoutWaived:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.InitialMarginWithoutWaived, curAccount, assetBalance));
            case MarginGroupEnum.StopOut:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.StopOut, curAccount, assetBalance));
            case MarginGroupEnum.LiveLeverage:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.LiveLeverage, curAccount, assetBalance));
            case MarginGroupEnum.BlockedForFixedIncome:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.BlockedForFixedIncome, curAccount, assetBalance));
            case MarginGroupEnum.FixedIncomeOrdersReq:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.FixedIncomeOrdersReq, curAccount, assetBalance));
        }
    }
    getItemFormattedValue(type, key) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        const value = this.getItemValue(type);
        switch (type) {
            case MarginGroupEnum.FundsUsed:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.FundsUsedPercent:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.MarginUsedNew:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.MarginUsed, curAccount, assetBalance));
            case MarginGroupEnum.StopOutPercent:
                return new RowItemDataStringArray(MathUtils.TruncateDouble(curAccount.MarginLevel, 2) + ' %');
            case MarginGroupEnum.CurMargin:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.MarginUsedPercent, curAccount, assetBalance));
            case MarginGroupEnum.MaintMargin:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.RiskLevelPercent, curAccount, assetBalance));
            case MarginGroupEnum.Leverage:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.MarginAvailableReal:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.MarginAvailable, curAccount, assetBalance));
            case MarginGroupEnum.MarginWarnPercent:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.MarginWarningLevel, curAccount, assetBalance));
            case MarginGroupEnum.MarginWarning:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.WarningMargin, curAccount, assetBalance));
            case MarginGroupEnum.MarginRequirement:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.MinMargin:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.BlockedByOrders:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.MarginStopOut:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.TotalMaintReq, curAccount, assetBalance));
            case MarginGroupEnum.StocksOrdersReq:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.StocksOrdersReq, curAccount, assetBalance));
            case MarginGroupEnum.MarginDeficiency:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.MarginSurplus:
                return new RowItemDataStringArray('');
            case MarginGroupEnum.BlockedForStocks:
                return new RowItemDataStringArray(assetBalance.formatPrice(curAccount.BlockedForStocksAccountCurrency));
            case MarginGroupEnum.MarginForOthers:
                return new RowItemDataStringArray(assetBalance.formatPrice(value.firstValue * curAccount.getCrossPrice()));
            case MarginGroupEnum.BlockedForStocksNew:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.BlockedForStocks, curAccount, assetBalance));
            case MarginGroupEnum.OptionPremiumReq:
                return new RowItemDataStringArray(assetBalance.formatPrice(assetBalance.OptionPremiumReq));
            case MarginGroupEnum.WaivedMargin:
                return new RowItemDataStringArray(value !== null && value.firstValue !== -1
                    ? assetBalance.formatPrice(value.firstValue)
                    : StringUtils.NOT_AVAILABLE);
            case MarginGroupEnum.WarningMarginReq:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.WarnMarginReq, curAccount, assetBalance));
            case MarginGroupEnum.WarningMarginReqPercent:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.WarnMarginReqPercent, curAccount, assetBalance));
            case MarginGroupEnum.MarginBeforeWarning:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.MarginBeforeWarning, curAccount, assetBalance));
            case MarginGroupEnum.InitialMarginWithoutWaived:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.InitialMarginWithoutWaived, curAccount, assetBalance));
            case MarginGroupEnum.StopOut:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.StopOut, curAccount, assetBalance));
            case MarginGroupEnum.LiveLeverage:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.LiveLeverage, curAccount, assetBalance, null, true));
            case MarginGroupEnum.BlockedForFixedIncome:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.BlockedForFixedIncome, curAccount, assetBalance));
            case MarginGroupEnum.FixedIncomeOrdersReq:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.FixedIncomeOrdersReq, curAccount, assetBalance));
        }
    }
    getItemNameKey(type) {
        switch (type) {
            case MarginGroupEnum.FundsUsed:
                return 'panel.accounts.FundsUsed';
            case MarginGroupEnum.FundsUsedPercent:
                return 'panel.accounts.FundsUsedPercent';
            case MarginGroupEnum.MarginUsedNew:
                return 'panel.accounts.MarginUsedNew';
            case MarginGroupEnum.StopOutPercent:
                return 'panel.accounts.stopOutPercent';
            case MarginGroupEnum.CurMargin:
                return 'panel.accounts.CurMargin';
            case MarginGroupEnum.MaintMargin:
                return 'panel.accounts.MaintMargin';
            case MarginGroupEnum.Leverage:
                return 'panel.accounts.Leverage';
            case MarginGroupEnum.MarginAvailableReal:
                return 'panel.accounts.MarginAvailableReal';
            case MarginGroupEnum.MarginWarnPercent:
                return 'panel.accounts.marginWarnPercent';
            case MarginGroupEnum.MarginWarning:
                return 'panel.accounts.MarginWarning';
            case MarginGroupEnum.MarginRequirement:
                return 'panel.accounts.MarginRequirement';
            case MarginGroupEnum.MinMargin:
                return 'panel.accounts.MinMargin';
            case MarginGroupEnum.BlockedByOrders:
                return 'panel.accounts.BlockedByOrders';
            case MarginGroupEnum.MarginStopOut:
                return 'panel.accounts.MarginStopOut';
            case MarginGroupEnum.StocksOrdersReq:
                return 'panel.accounts.StocksOrdersReq';
            case MarginGroupEnum.MarginDeficiency:
                return 'panel.accounts.MarginDeficiency';
            case MarginGroupEnum.MarginSurplus:
                return 'panel.accounts.MarginSurplus';
            case MarginGroupEnum.BlockedForStocks:
                return 'panel.accounts.BlockedForStocks';
            case MarginGroupEnum.MarginForOthers:
                return 'panel.accounts.MarginForOthers';
            case MarginGroupEnum.BlockedForStocksNew:
                return 'panel.accounts.BlockedForStocksNew';
            case MarginGroupEnum.OptionPremiumReq:
                return 'panel.accounts.OptionPremiumReq';
            case MarginGroupEnum.WaivedMargin:
                return 'panel.accounts.WaivedMargin';
            case MarginGroupEnum.WarningMarginReq:
                return 'panel.accounts.WarningMarginReq';
            case MarginGroupEnum.WarningMarginReqPercent:
                return 'panel.accounts.WarningMarginReqPercent';
            case MarginGroupEnum.MarginBeforeWarning:
                return 'panel.accounts.MarginBeforeWarning';
            case MarginGroupEnum.InitialMarginWithoutWaived:
                return 'panel.accounts.InitialMarginWithoutWaived';
            case MarginGroupEnum.StopOut:
                return 'panel.accounts.StopOut';
            case MarginGroupEnum.LiveLeverage:
                return 'panel.accounts.LiveLeverage';
            case MarginGroupEnum.BlockedForFixedIncome:
                return 'panel.accounts.BlockedForFixedIncome';
            case MarginGroupEnum.FixedIncomeOrdersReq:
                return 'panel.accounts.FixedIncomeOrdersReq';
        }
    }
    getItemRule(type) {
        switch (type) {
            case MarginGroupEnum.MarginUsedNew:
                return 'FUNCTION_ACCOUNT_DETAILS_INITIAL';
            case MarginGroupEnum.CurMargin:
                return 'FUNCTION_ACCOUNT_DETAILS_INITIAL_PERCENT';
            case MarginGroupEnum.MarginStopOut:
                return 'FUNCTION_ACCOUNT_DETAILS_MAINTENANCE';
            case MarginGroupEnum.MaintMargin:
                return 'FUNCTION_ACCOUNT_DETAILS_MAINTENANCE_PERCENT';
            case MarginGroupEnum.MarginAvailableReal:
                return 'FUNCTION_ACCOUNT_DETAILS_MARGIN_AVAILABLE';
            case MarginGroupEnum.MarginWarnPercent:
                return 'FUNCTION_ACCOUNT_DETAILS_MARGIN_WARNING_LEVEL';
            case MarginGroupEnum.MarginWarning:
                return 'FUNCTION_ACCOUNT_DETAILS_WARNING_MARGIN';
            case MarginGroupEnum.StopOutPercent:
                return 'FUNCTION_ACCOUNT_DETAILS_STOP_OUT_LEVEL';
            case MarginGroupEnum.BlockedForStocks:
            case MarginGroupEnum.BlockedForStocksNew:
                return 'FUNCTION_ACCOUNT_DETAILS_BLOCKED_FOR_STOCKS';
            case MarginGroupEnum.StocksOrdersReq:
                return 'FUNCTION_ACCOUNT_DETAILS_STOCK_ORDERS_REQ';
            case MarginGroupEnum.OptionPremiumReq:
                return 'FUNCTION_ACCOUNT_DETAILS_OPTION_PREMIUM_REQ';
            case MarginGroupEnum.WaivedMargin:
                return 'FUNCTION_ACCOUNT_DETAILS_WAIVED_MARGIN';
            case MarginGroupEnum.WarningMarginReq:
                return 'FUNCTION_ACCOUNT_DETAILS_WARNING';
            case MarginGroupEnum.WarningMarginReqPercent:
                return 'FUNCTION_ACCOUNT_DETAILS_WARNING_PERCENT';
            case MarginGroupEnum.MarginBeforeWarning:
                return 'FUNCTION_ACCOUNT_DETAILS_MARGIN_BEFORE_WARNING';
            case MarginGroupEnum.InitialMarginWithoutWaived:
                return 'FUNCTION_ACCOUNT_DETAILS_INITIAL_WITHOUT_WAIVED';
            case MarginGroupEnum.BlockedForFixedIncome:
                return 'FUNCTION_ACCOUNT_DETAILS_BLOCKED_FOR_FIXED_INCOME';
            case MarginGroupEnum.FixedIncomeOrdersReq:
                return 'FUNCTION_ACCOUNT_DETAILS_FIXED_INCOME_ORDERS_REQ';
            default:
                return null;
        }
    }
    getItemGroupRule() {
        return GROUP.MARGIN;
    }
    isHiddenItemRow(type) {
        if (super.isHiddenItemRow(type)) {
            return true;
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_EXT_RISK)) {
            return true;
        }
    }
    getColoringMethod(type) {
        if (type === MarginGroupEnum.LiveLeverage) {
            return VerticalPanelColoringTypes.LiveLeverage;
        }
        return VerticalPanelColoringTypes.Default;
    }
}
